import { ITmpWardRule } from "../../../@types/admin/AdminTmpIntegration";
import { useDeleteTmpWardRule } from "../../../hooks/mutations/admin/tmpIntegration";
import { useToast } from "../../../hooks/useToast";
import { errorToastMessage } from "../../../utils/HttpError";
import {
  FilterButton,
  ModalBodyText,
  ModalContainer,
  ModalForm,
  ModalFormActions,
  ModalTitle,
  WarningNotice,
} from "../shared";

interface IRemoveTmpWardRuleModalProps {
  rule: ITmpWardRule;
  onClose: () => void;
}

/**
 * Delete confirmation for a ward rule (EN-5203). Names the rule by its HCP
 * id and service — a bare "are you sure?" would not tell an operator which
 * of an HCP's rules they are about to remove.
 */
export function RemoveTmpWardRuleModal({
  rule,
  onClose,
}: IRemoveTmpWardRuleModalProps) {
  const { showToast } = useToast();
  const { deleteWardRule, isDeletingWardRule } = useDeleteTmpWardRule();

  const handleDelete = async () => {
    try {
      await deleteWardRule(rule.id);
      showToast("The ward rule was deleted successfully.", "success");
      onClose();
    } catch (error) {
      showToast(
        errorToastMessage(error, "There was an error deleting this ward rule."),
        "warning"
      );
    }
  };

  return (
    <ModalContainer data-testid="tmp-remove-ward-rule-modal">
      <ModalTitle>Delete prescriber rule</ModalTitle>
      <ModalForm>
        <ModalBodyText>
          Delete the rule for responsible HCP{" "}
          <strong>{rule.hcpHealthProviderId}</strong>
          {rule.serviceId ? (
            <>
              {" "}
              (service <strong>{rule.serviceId}</strong>)
            </>
          ) : null}{" "}
          to ward <strong>{rule.wardId}</strong>?
        </ModalBodyText>
        <WarningNotice>
          Orders from this HCP will be parked as PendingMapping until a new rule
          exists. Orders already provisioned are not affected.
        </WarningNotice>
        <ModalFormActions>
          <FilterButton
            variant="secondary"
            disabled={isDeletingWardRule}
            onClick={onClose}
          >
            Cancel
          </FilterButton>
          <FilterButton
            variant="primary"
            disabled={isDeletingWardRule}
            onClick={handleDelete}
            data-testid="tmp-remove-ward-rule-confirm"
          >
            {isDeletingWardRule ? "Deleting..." : "Delete"}
          </FilterButton>
        </ModalFormActions>
      </ModalForm>
    </ModalContainer>
  );
}
